import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Exercise } from '@/lib/types';
import { normalizeForSearch } from '@/lib/search';

interface UseExercisesOptions {
  muscleGroup?: string;
  search?: string;
}

export function useExercises(options: UseExercisesOptions = {}) {
  const { data: exercises = [], isLoading: loading, refetch } = useQuery({
    queryKey: ['exercises', options.muscleGroup ?? '', options.search ?? ''],
    queryFn: async () => {
      let query = supabase.from('exercises').select('*');
      if (options.muscleGroup) query = query.eq('muscle_group', options.muscleGroup) as any;
      // Busca normalizada (acentos + case) — usa coluna search_index + indice GIN (pack 0034)
      if (options.search) query = query.ilike('search_index', `%${normalizeForSearch(options.search)}%`) as any;
      const { data, error } = await (query as any).order('name');
      if (error) throw error;
      return data as Exercise[];
    },
  });

  return { exercises, loading, refetch };
}

/**
 * Lista distinta de muscle_group existentes na tabela exercises.
 * Buscada dinamicamente do banco — sempre sincronizada com o conteudo real.
 */
export function useMuscleGroups() {
  const { data: groups = [], isLoading: loading } = useQuery({
    queryKey: ['exercises-muscle-groups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('exercises')
        .select('muscle_group')
        .not('muscle_group', 'is', null);
      if (error) throw error;
      const unique = Array.from(new Set((data ?? []).map((r: { muscle_group: string }) => r.muscle_group))).sort();
      return unique;
    },
    staleTime: 5 * 60 * 1000,
  });
  return { groups, loading };
}
